import {
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  useTheme,
} from "@mui/material";
import { classes } from "portal/theme/theme";
import { useTranslation } from "react-i18next";
import React, { CSSProperties, FunctionComponent, useMemo } from "react";

export interface RadioChipOption {
  id: string;
  name: string;
  color?: {
    bg: string;
    text?: string;
  };
  icon?: React.ReactElement;
}
interface RadioChipsProps {
  isLoading: boolean;
  options: RadioChipOption[];
  activeId?: string;
  setActiveId: (id: string) => void;
  createLabel?: (option: { id: string; name: string }) => string;
  size?: "medium" | "small";
  forceDropdown?: boolean;
  className?: string;
}
export const RadioChips: FunctionComponent<RadioChipsProps> = ({
  isLoading = false,
  options,
  activeId,
  setActiveId,
  createLabel,
  size = "medium",
  forceDropdown = false,
  className,
}) => {
  const { t } = useTranslation();
  const muiTheme = useTheme();
  const colorsStyle = (
    isActive: boolean,
    color?: RadioChipOption["color"]
  ): CSSProperties => ({
    borderColor:
      !isActive && color?.bg
        ? color.bg
        : muiTheme.palette.getContrastText(muiTheme.palette.background.paper),
    color: isActive && color?.text ? color.text : muiTheme.palette.text.primary,
    backgroundColor: isActive ? color?.bg : undefined,
  });

  const activeCategory = useMemo(
    () => options.find((o) => o.id === activeId),
    [activeId, options]
  );

  const chipWrapperClasses = classes("flex flex-wrap", {
    "gap-2": size === "small",
    "gap-4": size === "medium",
  });
  return (
    <>
      <div
        className={classes("hidden", { "md:flex": !forceDropdown }, className)}
      >
        {isLoading ? (
          <div className={chipWrapperClasses}>
            {Array.from({ length: 5 })
              .fill(undefined)
              .map((_, index) => (
                <div key={index}>
                  <Skeleton
                    variant="rounded"
                    className="rounded-full h-6 w-28"
                  />
                </div>
              ))}
          </div>
        ) : (
          <div className={chipWrapperClasses}>
            {options.map(({ id, name, color, icon }) => (
              <Chip
                key={id}
                className={classes(
                  "font-normal [&_.MuiChip-icon]:text-inherit",
                  {
                    "p-1": size === "small",
                    "[&_.MuiChip-icon]:pl-1": icon,
                  }
                )}
                style={colorsStyle(activeId === id, color)}
                variant="outlined"
                icon={icon}
                label={createLabel?.({ id, name }) ?? name}
                onClick={() => {
                  setActiveId(id);
                }}
                size={size}
              />
            ))}
          </div>
        )}
      </div>
      <div
        className={classes("flex grow", {
          "md:hidden": !forceDropdown,
        })}
      >
        <Select<string>
          className={classes(
            "w-full rounded-full border-solid border-1",
            className,
            size === "small" ? "[&_.MuiSelect-select]:py-1" : undefined
          )}
          style={colorsStyle(true, activeCategory?.color)}
          size="small"
          defaultValue={options[0] ? options[0].id : ""}
          value={activeId ?? ""}
          onChange={(event: SelectChangeEvent) => {
            setActiveId(event.target.value as string);
          }}
          renderValue={(selected) => {
            if (!selected) {
              return t("utils.descriptors.none");
            }
            const selectedOption = options.find((o) => o.id === selected);
            if (!selectedOption) {
              return t("utils.descriptors.unknown");
            }
            const label = createLabel?.(selectedOption) ?? selectedOption.name;
            return selectedOption.icon ? (
              <span className="inline-flex items-center gap-1">
                {selectedOption.icon}
                {label}
              </span>
            ) : (
              label
            );
          }}
          displayEmpty
        >
          {options.map(({ name, id, icon }) => (
            <MenuItem value={id} key={id}>
              {icon ? (
                <span className="inline-flex items-center gap-1">
                  {icon}
                  {createLabel?.({ id, name }) ?? name}
                </span>
              ) : (
                createLabel?.({ id, name }) ?? name
              )}
            </MenuItem>
          ))}
        </Select>
      </div>
    </>
  );
};
